import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import SegmentChartCardKebabMenu from "./SegmentChartCardKebabMenu";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { mergeDebitNoteModel } from "./dashboardChartsModel";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import "./dashboardChartJsSetup";

const COL_PAID = "#5CC7C1";
const COL_BALANCE = "#E1F3F2";

function formatAmt(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(v));
}

function statusCellText(row, t) {
  const s = String(row?.status ?? "");
  if (s === "Paid") return t("Paid", { defaultValue: "Paid" });
  if (s === "Balance") return t("Balance", { defaultValue: "Balance" });
  return s;
}

/**
 * @param {object} props
 * @param {'installment'|'layby'} props.value
 * @param {(v: 'installment'|'layby') => void} props.onChange
 */
function DebitNoteScopeDropdown({ value, onChange }) {
  const { t } = useTranslation();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const options = useMemo(
    () => [
      { id: /** @type {'installment'} */ ("installment"), label: t("Installment", { defaultValue: "Installment" }) },
      { id: /** @type {'layby'} */ ("layby"), label: t("Layby", { defaultValue: "Layby" }) },
    ],
    [t],
  );

  const current = options.find((o) => o.id === value) ?? options[0];

  return (
    <div className="pos-dashboard-chart-card__groupby" ref={wrapRef}>
      <button
        type="button"
        className="pos-dashboard-chart-card__groupby-trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="pos-dashboard-chart-card__groupby-trigger-text">{current.label}</span>
        <span className="pos-dashboard-chart-card__groupby-chevron" aria-hidden>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M7 10l5 5 5-5" stroke="#8A8A8A" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
      {open ? (
        <div className="pos-dashboard-chart-card__groupby-menu" role="listbox">
          <div className="pos-dashboard-chart-card__groupby-menu-inner">
            {options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="option"
                aria-selected={value === opt.id}
                className={`pos-dashboard-chart-card__groupby-option${
                  value === opt.id ? " pos-dashboard-chart-card__groupby-option--selected" : ""
                }`}
                onClick={() => {
                  onChange(opt.id);
                  setOpen(false);
                }}
              >
                <span className="pos-dashboard-chart-card__groupby-option-row">{opt.label}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param {{ slice: import("./dashboardChartsModel.js").DebitNoteSliceModel }} props
 */
function DebitNoteDonutBody({ slice }) {
  const { t } = useTranslation();
  const paid = slice?.donut?.paid ?? 0;
  const balance = slice?.donut?.balance ?? 0;
  const total = paid + balance;
  const labelPaid = t("Paid", { defaultValue: "Paid" });
  const labelBal = t("Balance", { defaultValue: "Balance" });

  const chartData = useMemo(
    () => ({
      labels: [labelPaid, labelBal],
      datasets: [
        {
          data: [paid, balance],
          backgroundColor: [COL_PAID, COL_BALANCE],
          borderColor: "#FFFFFF",
          borderWidth: 1,
        },
      ],
    }),
    [balance, labelBal, labelPaid, paid],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      rotation: -90,
      circumference: 360,
      interaction: { mode: "nearest", intersect: true },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: () => "",
            label: (ctx) => {
              const i = ctx.dataIndex;
              const name = i === 0 ? labelPaid : labelBal;
              const amt = i === 0 ? paid : balance;
              const pct = total > 0 ? ((amt / total) * 100).toFixed(0) : "0";
              return `${name} : ${formatAmt(amt)} (${pct}%)`;
            },
          },
        },
      },
    }),
    [balance, labelBal, labelPaid, paid, total],
  );

  const centerTotal = slice?.summary?.totalValue ?? total;

  return (
    <div className="pos-debit-note-donut" aria-label={t("graphDebitNote", { defaultValue: "Debit Note" })}>
      <div className="pos-debit-note-donut__chart-wrap">
        <div className="pos-debit-note-donut__canvas">
          <Doughnut data={chartData} options={options} />
        </div>
        <div className="pos-debit-note-donut__center" aria-hidden>
          <span className="pos-debit-note-donut__center-label">
            {t("totalValue", { defaultValue: "Total Value" })}
          </span>
          <span className="pos-debit-note-donut__center-value">{formatAmt(centerTotal)}</span>
        </div>
      </div>
      <div className="pos-debit-note-donut__legend">
        <div className="pos-debit-note-donut__legend-block">
          <span className="pos-debit-note-donut__legend-item">
            <span className="pos-debit-note-donut__dot" style={{ background: COL_PAID }} aria-hidden />
            <span>{labelPaid}</span>
          </span>
          <span className="pos-debit-note-donut__legend-num">{formatAmt(paid)}</span>
          <span className="pos-debit-note-donut__legend-pct" style={{fontSize:'10px',color:'#A4A4A4'}}>
            ({total > 0 ? Math.round((100 * paid) / total) : 0}%)
          </span>
        </div>
        <div className="pos-debit-note-donut__legend-block">
          <span className="pos-debit-note-donut__legend-item">
            <span className="pos-debit-note-donut__dot" style={{ background: COL_BALANCE }} aria-hidden />
            <span>{labelBal}</span>
          </span>
          <span className="pos-debit-note-donut__legend-num">{formatAmt(balance)}</span>
          <span className="pos-debit-note-donut__legend-pct" style={{fontSize:'10px',color:'#A4A4A4'}}>
            ({total > 0 ? Math.round((100 * balance) / total) : 0}%)
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {{ slice: import("./dashboardChartsModel.js").DebitNoteSliceModel; cardTitle: string }} props
 */
function DebitNoteBarBody({ slice, cardTitle }) {
  const { t } = useTranslation();
  const paid = slice?.donut?.paid ?? 0;
  const balance = slice?.donut?.balance ?? 0;
  const totalVal = slice?.summary?.totalValue ?? paid + balance;
  const labelPaid = t("Paid", { defaultValue: "Paid" });
  const labelBal = t("Balance", { defaultValue: "Balance" });

  const yMax = useMemo(() => {
    const peak = Math.max(1, paid, balance);
    const step = 25000;
    const rounded = Math.ceil(peak / step) * step;
    return Math.max(100000, rounded);
  }, [balance, paid]);

  const { chartData, options } = useMemo(() => {
    const denom = paid + balance;
    const pctPaid = denom > 0 ? Math.round((100 * paid) / denom) : 0;
    const pctBal = denom > 0 ? Math.round((100 * balance) / denom) : 0;
    const data = {
      labels: ["", ""],
      datasets: [
        {
          label: labelPaid,
          data: [paid, balance],
          backgroundColor: [COL_PAID, COL_BALANCE],
          borderRadius: 0,
          borderSkipped: false,
          maxBarThickness: 56,
        },
      ],
    };
    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      layout: {
        padding: { top: 4, bottom: 10, left: 4, right: 4 },
      },
      datasets: {
        bar: {
          categoryPercentage: 0.55,
          barPercentage: 0.82,
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          align: "center",
          labels: {
            generateLabels: (chart) => {
              const ds = chart.data.datasets[0];
              const vals = ds?.data ?? [];
              const colors = Array.isArray(ds?.backgroundColor) ? ds.backgroundColor : [COL_PAID, COL_BALANCE];
              const p0 = Number(vals[0]) || 0;
              const p1 = Number(vals[1]) || 0;
              const tot = p0 + p1;
              const pp = tot > 0 ? Math.round((100 * p0) / tot) : 0;
              const pb = tot > 0 ? Math.round((100 * p1) / tot) : 0;
              return [
                {
                  text: `${labelPaid} ${formatAmt(p0)} (${pp}%)`,
                  fillStyle: colors[0],
                  strokeStyle: colors[0],
                  lineWidth: 0,
                  hidden: false,
                  index: 0,
                },
                {
                  text: `${labelBal} ${formatAmt(p1)} (${pb}%)`,
                  fillStyle: colors[1],
                  strokeStyle: colors[1],
                  lineWidth: 0,
                  hidden: false,
                  index: 1,
                },
              ];
            },
            boxWidth: 10,
            boxHeight: 10,
            usePointStyle: true,
            pointStyle: "circle",
            padding: 16,
            color: "#2A2A2A",
            font: { family: "'Inter', 'Segoe UI', sans-serif", size: 12, weight: "400" },
          },
        },
        tooltip: {
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          titleColor: "#434549",
          bodyColor: "#434549",
          borderColor: "#F2F2F2",
          borderWidth: 1,
          padding: 12,
          cornerRadius: 8,
          displayColors: true,
          callbacks: {
            title: () => "",
            label: (ctx) => {
              const i = ctx.dataIndex;
              const v = Number(ctx.parsed?.y) || 0;
              const name = i === 0 ? labelPaid : labelBal;
              const tot = paid + balance;
              const pct = tot > 0 && i === 0 ? pctPaid : tot > 0 ? pctBal : 0;
              return ` ${name}: ${formatAmt(v)} (${pct}%)`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { display: false },
          border: { display: false },
        },
        y: {
          min: 0,
          max: yMax,
          ticks: {
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 11 },
            callback: (v) => {
              const n = Number(v);
              if (!Number.isFinite(n)) return "";
              if (Math.abs(n) >= 1000) return `${Math.round(n / 1000)}k`;
              return String(n);
            },
          },
          grid: {
            color: "#E6E6E6",
            drawTicks: false,
          },
          border: { display: false },
        },
      },
    };
    return { chartData: data, options: opts };
  }, [balance, labelBal, labelPaid, paid, yMax]);

  return (
    <div className="pos-debit-note-bar" aria-label={cardTitle}>
      <div className="pos-debit-note-bar__summary">
        <span className="pos-debit-note-bar__summary-label">{t("totalValue", { defaultValue: "Total Value" })}</span>
        <span className="pos-debit-note-bar__summary-value">{formatAmt(totalVal)}</span>
      </div>
      <div className="pos-debit-note-bar__plot">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
}

/**
 * @param {{ slice: import("./dashboardChartsModel.js").DebitNoteSliceModel; rowIdPrefix: string }} props
 */
function DebitNoteTableBody({ slice, rowIdPrefix }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState(() => ({
    key: /** @type {"status"|"amount"|"pct"} */ ("status"),
    dir: /** @type {"asc"|"desc"} */ ("asc"),
  }));

  useEffect(() => {
    setSort({ key: "status", dir: "asc" });
  }, [slice]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  const sortedRows = useMemo(() => {
    const rows = (slice?.tableRows ?? []).map((r, i) => ({
      ...r,
      _i: i,
      _key: `${rowIdPrefix}-${r.status}-${i}`,
    }));
    const dirM = sort.dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      if (sort.key === "status") return statusCellText(a, t).localeCompare(statusCellText(b, t)) * dirM;
      if (sort.key === "amount") return (a.amount - b.amount) * dirM;
      return (a.pct - b.pct) * dirM;
    });
    return rows;
  }, [slice?.tableRows, sort, rowIdPrefix, t]);

  return (
    <div className="pos-debit-note-table" role="table" aria-label={t("graphDebitNote", { defaultValue: "Debit Note" })}>
      <div className="pos-debit-note-table__head" role="rowgroup">
        <div className="pos-debit-note-table__tr pos-debit-note-table__tr--head" role="row">
          <DashboardChartTableSortHeader
            baseClassName="pos-debit-note-table__th"
            sortedClassName="pos-debit-note-table__th--sorted"
            sorted={sort.key === "status"}
            direction={sort.dir}
            onSort={() => toggleSort("status")}
            ariaSortLabel={t("Sort by Status", { defaultValue: "Sort by Status" })}
            className="pos-debit-note-table__th--first"
          >
            {t("Status", { defaultValue: "Status" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName="pos-debit-note-table__th"
            sortedClassName="pos-debit-note-table__th--sorted"
            sorted={sort.key === "amount"}
            direction={sort.dir}
            onSort={() => toggleSort("amount")}
            ariaSortLabel={t("Sort by Amount", { defaultValue: "Sort by Amount" })}
            className="pos-debit-note-table__th--num"
          >
            {t("Amount", { defaultValue: "Amount" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName="pos-debit-note-table__th"
            sortedClassName="pos-debit-note-table__th--sorted"
            sorted={sort.key === "pct"}
            direction={sort.dir}
            onSort={() => toggleSort("pct")}
            ariaSortLabel={t("Sort by percent", { defaultValue: "Sort by percent" })}
            className="pos-debit-note-table__th--pct"
          >
            %
          </DashboardChartTableSortHeader>
        </div>
      </div>
      <div className="pos-debit-note-table__scroll" role="rowgroup">
        {sortedRows.map((row) => (
          <div key={row._key} className="pos-debit-note-table__tr" role="row">
            <div className="pos-debit-note-table__td pos-debit-note-table__td--status" role="cell">
              {statusCellText(row, t)}
            </div>
            <div className="pos-debit-note-table__td pos-debit-note-table__td--amount" role="cell">
              {formatAmt(row.amount)}
            </div>
            <div className="pos-debit-note-table__td pos-debit-note-table__td--pct" role="cell">
              {row.pct}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {import("./dashboardChartsModel.js").DebitNoteChartModel | null | undefined} [props.model]
 * @param {() => void} [props.onMenuRemove]
 */
function DebitNoteChartCard({ model, onMenuRemove }) {
  const { t } = useTranslation();
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [viewKind, setViewKind] = useState(/** @type {"donut"|"bar"|"table"} */ ("donut"));
  const [scope, setScope] = useState(/** @type {"installment"|"layby"} */ ("installment"));
  const merged = useMemo(() => mergeDebitNoteModel(model && typeof model === "object" ? model : null), [model]);
  const slice = scope === "layby" ? merged.layby : merged.installment;
  const cardTitle = t("graphDebitNote", { defaultValue: "Debit Note" });
  const donutMenuLabel = t("Dashboard", { defaultValue: "Dashboard" });

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("debit-note"));
  }, []);

  return (
    <div
      className={`pos-dashboard-chart-card pos-dashboard-chart-card--debit-note${
        viewKind === "table" ? " pos-dashboard-chart-card--debit-note-table" : ""
      }`}
    >
      <header className="pos-dashboard-chart-card__header">
        <h2 className="pos-dashboard-chart-card__title">{cardTitle}</h2>
        <div className="pos-dashboard-chart-card__header-actions">
          <DebitNoteScopeDropdown value={scope} onChange={setScope} />
          <SegmentChartCardKebabMenu
            viewKind={viewKind}
            onViewKindChange={setViewKind}
            onDownload={handleDownloadPng}
            onRemove={onMenuRemove}
            donutMenuLabel={donutMenuLabel}
          />
        </div>
      </header>
      <div
        className={`pos-dashboard-chart-card__body pos-dashboard-chart-card__body--debit-note${
          viewKind === "table" ? " pos-dashboard-chart-card__body--debit-note-table" : ""
        }${viewKind === "bar" ? " pos-dashboard-chart-card__body--debit-note-bargraph" : ""}${
          viewKind === "donut" ? " pos-dashboard-chart-card__body--debit-note-donut" : ""
        }`}
      >
        <div ref={captureRef} className="pos-dashboard-chart-card__capture pos-dashboard-chart-card__capture--debit-note">
          {viewKind === "donut" ? <DebitNoteDonutBody slice={slice} /> : null}
          {viewKind === "bar" ? <DebitNoteBarBody slice={slice} cardTitle={cardTitle} /> : null}
          {viewKind === "table" ? <DebitNoteTableBody slice={slice} rowIdPrefix={`dn-${scope}`} /> : null}
        </div>
      </div>
    </div>
  );
}

export default DebitNoteChartCard;
